import { ComponentType, useCallback, useMemo } from "react";
import ReactSelect, { GroupBase, Props, StylesConfig } from "react-select";
import { CreatableProps } from "react-select/creatable/dist/react-select-creatable.cjs";
import { AsyncPaginate, AsyncPaginateProps, LoadOptions, wrapMenuList } from "react-select-async-paginate";
import { genericMemo } from "../../../../utils";
import { Input, InputProps } from "../Input";
import { ClearIndicator } from "./ClearIndicator";
import { getStyles } from "./customStyles";
import { DropdownIndicator } from "./DropdownIndicator";
import { MenuList } from "./MenuList";
import { MenuPortal } from "./MenuPortal";

import stylesCSS from "./style.module.scss";

export type StylesMergePolicy = "override" | "merge";


type BaseSelectProps<T, Group extends GroupBase<T> = GroupBase<T>, IsMulti extends boolean = boolean, Additional = any> =
  CreatableProps<T, IsMulti, Group> &
  Omit<Props<T> & AsyncPaginateProps<T, Group, Additional, IsMulti>, (
    "isDisabled" | "isClearable" | "menuPortalTarget" | "menuPlacement" |
    "menuPosition" | "loadOptions"
    )>; // eslint-disable-line @typescript-eslint/indent

export type SelectProps<T, Group extends GroupBase<T> = GroupBase<T>, IsMulti extends boolean = boolean, Additional = any> =
  InputProps<BaseSelectProps<T, GroupBase<T>, IsMulti>>
  &
  {
    loadOptions?: LoadOptions<T, Group, Additional>;
    stylesMergePolicy?: StylesMergePolicy
    useAsyncSelect?: boolean,
    useValueFromOptionsFirst?: boolean,
  }

export const Select = genericMemo(<T extends any, Group extends GroupBase<T> = GroupBase<T>>(
  {
    value,
    onChange,
    styles,
    stylesMergePolicy,
    additional,
    getOptionLabel,
    components,
    useAsyncSelect,
    useValueFromOptionsFirst,
    options,
    getOptionValue,
    ...props
  }: SelectProps<T, Group>) => {

  const selectStyles: StylesConfig<T, boolean> = useMemo(
    () => getStyles<T, boolean>(styles || {}, stylesMergePolicy as StylesMergePolicy),
    [ styles, stylesMergePolicy ],
  );

  const selectAdditional = useMemo(() => ({
    page: 1,
    ...additional,
  }), [ additional ]);


  const selectComponents = useMemo(() => ({
    ClearIndicator,
    DropdownIndicator,
    IndicatorSeparator: null,
    MenuList: useAsyncSelect ? wrapMenuList(MenuList as any) : MenuList,
    MenuPortal,
    ...components,
  }), [ components, useAsyncSelect ]);


  const formatOptionLabel = useCallback((data: any, { context }: any) => {
    const value = getOptionLabel ? getOptionLabel(data) : (data.label ?? data?.toString());
    if (context === "menu") {
      return (<span className={stylesCSS.optionContent} title={value}>{value}</span>);
    }
    return value;
  }, [ getOptionLabel ]);

  const selectValue = useMemo(() => {
    if (!useValueFromOptionsFirst || !value) {
      return value;
    }


    //todo remove
    return options?.map((v) => {
      // @ts-ignore
      return (v.options ? v.options : [ v ]) as T[];
    })
      .reduce((acc, v) => [ ...acc, ...v ], [])
      .find((option) => {
        if (getOptionValue) {
          // @ts-ignore
          return getOptionValue(option) === getOptionValue(value);
        }
        return option === value;
      }) || value;
  }, [ useValueFromOptionsFirst, value, options, getOptionValue ]);
  return (
    <Input
      mapPropsToInput={({ inputProps }: any) => ({
        ...inputProps,
        id: inputProps.id ? inputProps.id + "-container" : undefined,
        inputId: inputProps.id ? inputProps.id : undefined,
        isClearable: props.isClearable,
        isDisabled: props.disabled,
        customMenuShouldBlockScroll: props.menuShouldBlockScroll ?? true,
        menuPortalTarget: document.body,
      })}
      value={selectValue === undefined ? (props.isMulti ? [] : null) : selectValue}
      onChange={onChange as any}
      styles={selectStyles}
      additional={selectAdditional}
      components={selectComponents as any}
      getOptionLabel={getOptionLabel}
      formatOptionLabel={formatOptionLabel}
      options={options}
      getOptionValue={getOptionValue}
      {...props}
      component={props.component ?? (useAsyncSelect ? AsyncPaginate : ReactSelect) as any}
      //menuShouldBlockScroll should be always false to avoid ScrollManager render
      menuShouldBlockScroll={false}
      showErrorIcon={false}
      isClearable={false}
    />
  );
});


(Select as ComponentType<any>).defaultProps = {
  stylesMergePolicy: "merge",
  useAsyncSelect: true,
  useValueFromOptionsFirst: false,
};

export default Select;
